import { useState, useEffect } from "react";
import { Typography, CircularProgress, Box } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../stores";
import { checkHookPermission } from "../../features/estimator-hook-module/estimatorHookAction";
import EmbedViewGeneratedReport, {
  ViewGeneratedReportState,
} from "../estimator-hook/EmbedViewGeneratedReport";
import EmbedGenerateReport from "../estimator-hook/EmbedGenerateReport";

const EmbeddedEstimator = () => {
  const [
    reportState,
    setReportState,
  ] = useState<ViewGeneratedReportState | null>(null);
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.estimatorHook);
  const [allowed, setAllowed] = useState(false);

  // Extract clientId from the URL path
  const clientId = window.location.pathname.split("/").pop();

  useEffect(() => {
    const checkPermission = async () => {
      if (!clientId) {
        console.error("Invalid client ID:", clientId);
        dispatch({
          type: checkHookPermission.rejected.type,
          payload: "Invalid client ID.",
        });
        return;
      }

      try {
        const referrer = document.referrer || window.location.origin;
        console.log("Checking permission with:", { clientId, referrer });
        await dispatch(
          checkHookPermission({
            clientId,
            referrer,
          })
        ).unwrap();
        setAllowed(true);
      } catch (err) {
        console.error("Permission check failed:", err);
        setAllowed(false);
      }
    };
    checkPermission();
  }, [clientId, dispatch]);

  const handleCalculate = (state: ViewGeneratedReportState) => {
    setReportState(state);
  };

  const handleBack = () => {
    setReportState(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !allowed) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="error">
          {error ||
            "Not authorized to embed this estimator. Please ensure the client ID is valid or contact support."}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "#757575" }}>
          If you believe this is an error, contact support with your client ID.
        </Typography>
      </Box>
    );
  }

  return reportState ? (
    <EmbedViewGeneratedReport state={reportState} onBack={handleBack} />
  ) : (
    <EmbedGenerateReport onSubmit={handleCalculate} clientId={clientId ?? ""} />
  );
};

export default EmbeddedEstimator;
